import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Code, 
  Database, 
  Globe, 
  Cpu, 
  Cloud, 
  Smartphone,
  Zap,
  Shield,
  Layers,
  Settings
} from 'lucide-react';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const skillCategories = [
    
    {
      name: 'Languages',
      icon: Code,
      skills: ['Python', 'JavaScript', 'Java', 'C#', 'Dart', 'C++', 'HTML', 'CSS']
    },
    {
      name: 'Frameworks & Libraries',
      icon: Layers,
      skills: ['Node.js', 'Django REST Framework','Flask',  'React', 'Flutter', 'Bootstrap']
    },
    {
      name: 'Tools & Platforms',
      icon: Cloud,
      skills: ['Git', 'GitHub', 'Render', 'Vercel', 'Cloudinary', 'AWS (Basics)', 'JupyterLab']
    },
    {
      name: 'Databases',
      icon: Database,
      skills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis']
    },
    {
      name: 'AI & Data Science',
      icon: Cpu,
      skills: ['AI Tools', 'Prompting', 'NumPy', 'Pandas']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="skills" className="py-20 px-4 relative">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/3 left-1/3 w-96 h-96 bg-gradient-to-r from-primary-gold/3 to-primary-violet/3 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Zap className="w-8 h-8 text-primary-gold" />
            <h2 className="section-title gradient-text">Skills & Technologies</h2>
            <Shield className="w-8 h-8 text-primary-gold" />
          </div>
          <p className="section-subtitle">My developer arsenal</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.name}
              variants={itemVariants}
              className="glass-effect p-6 rounded-2xl group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary-gold/10 rounded-lg group-hover:bg-primary-gold/20 transition-colors">
                  <category.icon className="w-6 h-6 text-primary-gold" />
                </div>
                <h3 className="text-xl font-bold text-primary-gold font-playfair">
                  {category.name}
                </h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="skill-pill cursor-pointer"
                  >
                    {skill}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Skills Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16"
        >
          <div className="glass-effect p-8 rounded-2xl">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-primary-gold" />
              <h3 className="text-2xl font-bold text-primary-gold text-center font-playfair">
                Development Focus Areas
              </h3>
              <Smartphone className="w-6 h-6 text-primary-gold" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
  {
    title: "Frontend",
    icon: Globe,
    desc: "React, Flutter, HTML, CSS"
  },
  {
    title: "Backend",
    icon: Code,
    desc: "Node.js, Django, Flask, REST APIs"
  },
  {
    title: "Database",
    icon: Database,
    desc: "PostgreSQL, MySQL, MongoDB, Redis"
  },
  {
    title: "Cloud & Deployment",
    icon: Cloud,
    desc: "Vercel, Railway, OnRender, Cloudinary, Docker, AWS(basics)"
  }
]
.map((area, index) => (
                <motion.div
                  key={area.title}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  className="text-center group"
                >
                  <div className="p-4 bg-primary-gold/5 rounded-xl mb-3 group-hover:bg-primary-gold/10 transition-colors">
                    <area.icon className="w-8 h-8 text-primary-gold mx-auto" />
                  </div>
                  <h4 className="text-lg font-semibold text-primary-text mb-2">{area.title}</h4>
                  <p className="text-sm text-primary-muted">{area.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 